import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLElement} [paginationControls] - The numbered pagination nav (paged mode).
 * @property {HTMLElement} [showAll] - Wrapper of the "Show all products" button (ECS, GLOSS-75).
 * @property {HTMLButtonElement} [showAllButton] - The "Show all products" button (ECS, GLOSS-75).
 * @property {HTMLElement} [showAllStatus] - Polite status region for the show-all run (ECS, GLOSS-75).
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * ECS (GLOSS-75): bumped on every filter/sort update of this section so an
   * in-flight "Show all products" run stops adding pages from the old results.
   */
  #showAllGeneration = 0;

  /** ECS (GLOSS-75): true while a "Show all products" run is in flight. */
  #showAllRunning = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @param {boolean} [useCache] - Whether the section renderer may answer from its cache (ECS, GLOSS-75)
   */
  async #fetchSpecificPage(pageNumber, url = undefined, useCache = true) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, useCache, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    await yieldToMainThread();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * ECS (GLOSS-75): "Show all products" (`on:click="/showAllPages"` in
   * snippets/product-grid.liquid). Loads every page of the current result set
   * that is not in the grid yet, one request at a time and in page order,
   * without touching the URL: later pages are appended, and earlier pages
   * (when the shopper started past page 1) are prepended with the scroll
   * position held. When every page is in, the pagination nav and the button
   * are removed and the result is announced. On a failed page the button is
   * re-enabled and the pages that did load stay in the grid.
   */
  async showAllPages() {
    const { grid, cards, showAllButton, showAllStatus } = this.refs;

    if (!grid || !showAllButton || this.#showAllRunning) return;
    if (!Array.isArray(cards) || cards.length === 0) return;

    const lastPage = Number(grid.dataset.lastPage);
    const firstShownPage = Number(cards[0]?.dataset.page);
    const lastShownPage = Number(cards[cards.length - 1]?.dataset.page);

    if (!lastPage || !firstShownPage || !lastShownPage) return;

    const generation = this.#showAllGeneration;
    const isCurrentRun = () => generation === this.#showAllGeneration && this.isConnected && this.refs.grid === grid;

    this.#showAllRunning = true;
    this.#setShowAllBusy(showAllButton, true);
    this.#announce(showAllStatus, showAllButton.dataset.loadingMessage);

    /** @type {Element | undefined} */
    let firstAppendedItem;

    try {
      for (let page = lastShownPage + 1; page <= lastPage; page++) {
        const items = await this.#loadPageItems(page);
        if (!isCurrentRun()) return;

        if (!firstAppendedItem) firstAppendedItem = items[0];
        grid.append(...items);
        this.#aspectRatioHelper?.processNewElements();
        await yieldToMainThread();
      }

      for (let page = firstShownPage - 1; page >= 1; page--) {
        const items = await this.#loadPageItems(page);
        if (!isCurrentRun()) return;

        this.#prependKeepingScroll(grid, items);
        await yieldToMainThread();
      }
    } catch (error) {
      console.error(error);
      if (!isCurrentRun()) return;

      this.#setShowAllBusy(showAllButton, false);
      this.#announce(showAllStatus, showAllButton.dataset.errorMessage);
      return;
    } finally {
      this.#showAllRunning = false;
    }

    // Keep keyboard focus in the list: the button it sits on is about to go.
    const buttonHadFocus = document.activeElement === showAllButton;
    const focusItem = firstAppendedItem ?? grid.querySelector(':scope > [ref="cards[]"]');
    const focusTarget = focusItem?.querySelector('a[href]');

    this.refs.paginationControls?.remove();
    (this.refs.showAll ?? showAllButton).remove();
    this.#announce(showAllStatus, showAllButton.dataset.doneMessage);

    if (buttonHadFocus && focusTarget instanceof HTMLElement) focusTarget.focus();
  }

  /**
   * ECS (GLOSS-75): the grid items of one page, fetched through the Section
   * Rendering API unless already prefetched.
   * @param {number} page
   * @returns {Promise<Element[]>}
   */
  async #loadPageItems(page) {
    // Bypass the section renderer's cache so a retry after a bad response refetches.
    if (!this.pages.has(page)) await this.#fetchSpecificPage(page, undefined, false);

    const items = this.#getGridForPage(page);

    if (!items || items.length === 0) {
      this.pages.delete(page);
      throw new Error(`Page ${page} of the results could not be loaded`);
    }

    return [...items];
  }

  /**
   * ECS (GLOSS-75): prepends items to the grid while holding the viewport on
   * the content the shopper is looking at (same approach as #renderPreviousPage,
   * anchored on the first card rather than the grid's first child, which can
   * be the inline layout script).
   * @param {HTMLElement} grid
   * @param {Element[]} items
   */
  #prependKeepingScroll(grid, items) {
    const anchor = grid.querySelector(':scope > [ref="cards[]"]');
    const currentScrollTop = getScrollTop();
    const oldTop = anchor ? anchor.getBoundingClientRect().top + currentScrollTop : 0;

    grid.prepend(...items);
    this.#aspectRatioHelper?.processNewElements();

    if (anchor) {
      const newTop = anchor.getBoundingClientRect().top + getScrollTop();
      scrollTo({ top: currentScrollTop + (newTop - oldTop), behavior: 'instant' });
    }
  }

  /**
   * ECS (GLOSS-75): busy state for the show-all button. `aria-disabled` rather
   * than `disabled` so a keyboard user's focus stays on the button while it runs.
   * @param {HTMLButtonElement} button
   * @param {boolean} busy
   */
  #setShowAllBusy(button, busy) {
    if (busy) {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * ECS (GLOSS-75): writes a message into a polite status region, clearing it
   * first so a repeated message (e.g. a second error) is announced again.
   * @param {HTMLElement | undefined} region
   * @param {string | undefined} message
   */
  #announce(region, message) {
    if (!region || !message) return;

    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();
    this.#showAllGeneration++; // ECS (GLOSS-75): stop any in-flight show-all run

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
